import React, { useState, useEffect } from 'react';
import { notify, confirmDialog } from '../ui/notify';
import { apiCall, withToken } from '../config';
import FencePDFViewer from './FencePDFViewer';
import StationPlanner2D from './StationPlanner2D';
import StationLayoutManager from './StationLayoutManager';
import SignageManager from './SignageManager';

const TasksAndPlanning = ({ user, initialTaskId, onDeepTaskConsumed }) => {
  const [activeTab, setActiveTab] = useState('tasks');
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [internalDocuments, setInternalDocuments] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [documentFolders, setDocumentFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [taskCategories, setTaskCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Task Management States
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'todo',
    due_date: '',
    category_id: '',
    assigned_to: ''
  });

  // Notizen pro Aufgabe (Verlauf: wer, wann, was)
  const [taskNotes, setTaskNotes] = useState([]);
  const [newNote, setNewNote] = useState('');

  // Board-States: Drag & Drop, Filter, Handy-Ansicht, Zuweisung
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [taskSearch, setTaskSearch] = useState('');
  const [taskFilterCat, setTaskFilterCat] = useState('all');
  const [taskFilterPrio, setTaskFilterPrio] = useState('all');
  const [dragTaskId, setDragTaskId] = useState(null);
  const [dragOverStatus, setDragOverStatus] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [mobileStatus, setMobileStatus] = useState('pending');

  // Task Categories Management States
  const [showTaskCategoryModal, setShowTaskCategoryModal] = useState(false);
  const [editingTaskCategory, setEditingTaskCategory] = useState(null);
  const [taskCategoryForm, setTaskCategoryForm] = useState({
    name: '',
    description: '',
    color: '#007bff',
    icon: 'bi-list-task'
  });

  // Reset Management States
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetForm, setResetForm] = useState({
    year: new Date().getFullYear() + 1,
    resetStatus: true,
    adjustDueDates: true,
    resetAssignments: false,
    categories: 'all',
    preserveCompleted: false
  });
  const [resetPreview, setResetPreview] = useState(null);

  // Document Management States
  const [showDocModal, setShowDocModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [docForm, setDocForm] = useState({
    category: '',
    folder_id: '',
    description: ''
  });

  // Document Search States
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Drag & Drop States
  const [isDragOver, setIsDragOver] = useState(false);
  const [draggedFiles, setDraggedFiles] = useState([]);

  // Folder Management States
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderForm, setFolderForm] = useState({
    name: '',
    description: '',
    parent_id: '',
    category_id: '',
    color: '#007bff',
    icon: 'bi-folder'
  });

  // Category Management States
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    color: '#007bff',
    icon: 'bi-folder'
  });

  // Material Management States
  const [materials, setMaterials] = useState([]);
  const [materialCategories, setMaterialCategories] = useState([]);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showMaterialCategoryModal, setShowMaterialCategoryModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [editingMaterialCategory, setEditingMaterialCategory] = useState(null);
  const [materialForm, setMaterialForm] = useState({
    name: '',
    description: '',
    category_id: '',
    type: 'consumable',
    unit: '',
    current_stock: '',
    min_stock: '',
    max_stock: '',
    cost_per_unit: '',
    supplier: '',
    location: '',
    notes: ''
  });
  const [materialCategoryForm, setMaterialCategoryForm] = useState({
    name: '',
    description: '',
    type: 'consumable',
    color: '#007bff',
    icon: 'bi-box'
  });
  const [materialFilter, setMaterialFilter] = useState('all'); // 'all', 'consumable', 'inventory'

  // Expense Management States
  const [expenses, setExpenses] = useState([]);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    from_person: '',
    reason: '',
    date: '',
    amount: '',
    completed: false
  });

  // Bauzaun Management States
  const [fences, setFences] = useState([]);
  const [selectedStation, setSelectedStation] = useState('G1');
  const [viewMode, setViewMode] = useState('2d'); // '2d' oder '3d'
  const [stationPlans, setStationPlans] = useState({
    G1: null,
    G5: null,
    G8: null,
    G10: null
  });
  const [showFenceModal, setShowFenceModal] = useState(false);
  const [editingFence, setEditingFence] = useState(null);
  const [fenceForm, setFenceForm] = useState({
    station: 'G1',
    number_from: '',
    number_to: '',
    x: 0,
    y: 0,
    width: 100,
    height: 30,
    is_short: false,
    color: '#ffc107'
  });
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [canvasRef, setCanvasRef] = useState(null);
  const [pdfPage, setPdfPage] = useState(null);
  const [selectedFence, setSelectedFence] = useState(null);

  const priorities = {
    low: { label: 'Niedrig', class: 'success', icon: '⬇️' },
    medium: { label: 'Mittel', class: 'warning', icon: '➡️' },
    high: { label: 'Hoch', class: 'danger', icon: '⬆️' },
    urgent: { label: 'Dringend', class: 'danger', icon: '🚨' }
  };

  const statuses = {
    todo: { label: 'Noch zu planen', class: 'secondary', icon: '📥' },
    pending: { label: 'Ausstehend', class: 'warning', icon: '⏳' },
    inprogress: { label: 'In Bearbeitung', class: 'info', icon: '⚙️' },
    completed: { label: 'Erledigt', class: 'success', icon: '✅' },
    cancelled: { label: 'Abgebrochen', class: 'dark', icon: '❌' }
  };

  // Prioritäts-Farbstreifen an der linken Kartenkante + Sortiergewicht
  const prioColors = { low: '#198754', medium: '#ffc107', high: '#fd7e14', urgent: '#dc3545' };
  const prioWeight = { urgent: 0, high: 1, medium: 2, low: 3 };

  const availableIcons = [
    'bi-folder', 'bi-file-earmark', 'bi-book', 'bi-journal-text', 'bi-calendar-check',
    'bi-list-check', 'bi-gear', 'bi-graph-up', 'bi-people', 'bi-shield-check',
    'bi-star', 'bi-heart', 'bi-lightbulb', 'bi-flag', 'bi-archive', 'bi-list-task',
    'bi-calendar2-check', 'bi-arrow-repeat', 'bi-exclamation-triangle', 'bi-box',
    'bi-box-seam', 'bi-toolbox', 'bi-wrench', 'bi-hammer', 'bi-scissors',
    'bi-pencil-fill', 'bi-droplet-fill', 'bi-shield-exclamation', 'bi-laptop',
    'bi-tools', 'bi-clipboard-data', 'bi-cart3', 'bi-truck'
  ];

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchTasks();
      fetchTaskCategories();
      fetchAssignableUsers();
      fetchInternalDocuments();
      fetchDocumentCategories();
      fetchMaterials();
      fetchMaterialCategories();
      fetchExpenses();
      // fetchFences(); // Entfernt - wird jetzt von StationPlanner2D gehandhabt
    }
  }, [user]);

  // Handy-Erkennung fürs Board (Spalten → Status-Tabs)
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Deep-Link (?task=30): sobald die Aufgaben geladen sind, Karte direkt öffnen
  useEffect(() => {
    if (!initialTaskId || tasks.length === 0) return;
    const t = tasks.find((x) => Number(x.id) === Number(initialTaskId));
    if (t) handleEditTask(t);
    if (onDeepTaskConsumed) onDeepTaskConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTaskId, tasks]);

  // Ordner laden wenn eine Kategorie ausgewählt wird
  useEffect(() => {
    if (docForm.category) {
      // Kategorie-ID aus dem Namen ermitteln
      const selectedCategory = documentCategories.find(cat => cat.name === docForm.category);
      if (selectedCategory) {
        fetchDocumentFolders(selectedCategory.id);
      }
    }
  }, [docForm.category, documentCategories]);

  // Material-Filter hat sich geändert
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchMaterials();
    }
  }, [materialFilter]);

  const fetchTasks = async () => {
    try {
      console.log('🔍 Frontend: Fetching tasks...');
      setLoading(true);
      const data = await apiCall('/admin/tasks');
      console.log('🔍 Frontend: Received tasks:', data);
      setTasks(data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Aufgaben:', error);
      setMessage('Fehler beim Laden der Aufgaben');
    } finally {
      setLoading(false);
    }
  };

  const fetchInternalDocuments = async () => {
    try {
      const data = await apiCall('/admin/internal-documents');
      setInternalDocuments(data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Dokumente:', error);
    }
  };

  const fetchDocumentCategories = async () => {
    try {
      const data = await apiCall('/admin/document-categories');
      setDocumentCategories(data || []);
      // Erste Kategorie als Standard setzen, wenn noch keine ausgewählt
      if (!docForm.category && data && data.length > 0) {
        setDocForm(prev => ({ ...prev, category: data[0].name })); // Namen verwenden (wie ursprünglich)
      }
    } catch (error) {
      console.error('Fehler beim Laden der Kategorien:', error);
    }
  };

  const fetchDocumentFolders = async (categoryId) => {
    try {
      const data = await apiCall(`/admin/document-folders/${categoryId}`);
      setDocumentFolders(data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Ordner:', error);
      setDocumentFolders([]);
    }
  };

  // Zuweisbare Personen fürs Aufgaben-Board (Leitung zuerst)
  const fetchAssignableUsers = async () => {
    try {
      const data = await apiCall('/users');
      const list = (Array.isArray(data) ? data : []).sort((a, b) =>
        ((a.role_id === 1 ? 0 : 1) - (b.role_id === 1 ? 0 : 1)) ||
        String(a.firstname || '').localeCompare(String(b.firstname || ''))
      );
      setAssignableUsers(list);
    } catch (error) {
      console.error('Fehler beim Laden der zuweisbaren Personen:', error);
    }
  };

  const fetchTaskCategories = async () => {
    try {
      const data = await apiCall('/admin/task-categories');
      setTaskCategories(data || []);
      // Erste Kategorie als Standard setzen, wenn noch keine ausgewählt
      if (!taskForm.category_id && data && data.length > 0) {
        setTaskForm(prev => ({ ...prev, category_id: data[0].id }));
      }
    } catch (error) {
      console.error('Fehler beim Laden der Task-Kategorien:', error);
    }
  };

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const queryParams = materialFilter !== 'all' ? `?type=${materialFilter}` : '';
      const data = await apiCall(`/admin/materials${queryParams}`);
      setMaterials(data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Materialien:', error);
      setMessage('Fehler beim Laden der Materialien');
    } finally {
      setLoading(false);
    }
  };

  const fetchMaterialCategories = async () => {
    try {
      const data = await apiCall('/admin/material-categories');
      setMaterialCategories(data || []);
      // Erste Kategorie als Standard setzen, wenn noch keine ausgewählt
      if (!materialForm.category_id && data && data.length > 0) {
        setMaterialForm(prev => ({ ...prev, category_id: data[0].id }));
      }
    } catch (error) {
      console.error('Fehler beim Laden der Material-Kategorien:', error);
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const data = await apiCall('/admin/expenses');
      setExpenses(data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Auslagen:', error);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  // Alte fetchFences Funktion entfernt - wird jetzt von StationPlanner2D gehandhabt

  const handleSaveTask = async () => {
    try {
      if (!taskForm.title.trim()) {
        setMessage('Bitte geben Sie einen Titel ein');
        return;
      }

      const url = editingTask ? `/admin/tasks/${editingTask.id}` : '/admin/tasks';
      const method = editingTask ? 'PUT' : 'POST';

      const payload = {
        ...taskForm,
        category_id: taskForm.category_id || null,
        assigned_to: taskForm.assigned_to ? Number(taskForm.assigned_to) : null,
        assigned_by: editingTask ? editingTask.assigned_by : (user?.id || null),
        edited_by: user?.id || null, // wer gerade speichert – gegen Selbst-Zuweisungs-Pings
        due_date: taskForm.due_date || null
      };
      console.log('🔍 Frontend: Saving task...', payload);
      await apiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      setMessage(editingTask ? 'Aufgabe aktualisiert' : 'Aufgabe erstellt');
      setShowTaskModal(false);
      setEditingTask(null);
      setTaskForm({
        title: '',
        description: '',
        priority: 'medium',
        status: 'todo',
        due_date: '',
        category_id: taskCategories.length > 0 ? taskCategories[0].id : '',
        assigned_to: ''
      });
      
      console.log('🔍 Frontend: Task saved, now fetching tasks...');
      // Kleiner Delay um sicherzustellen dass die DB aktualisiert wurde
      setTimeout(() => {
        fetchTasks();
      }, 100);
    } catch (error) {
      console.error('Fehler beim Speichern der Aufgabe:', error);
      setMessage('Fehler beim Speichern der Aufgabe');
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title || '',
      description: task.description || '',
      priority: task.priority || 'medium',
      status: task.status || 'todo',
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      category_id: task.category_id || (taskCategories.length > 0 ? taskCategories[0].id : ''),
      assigned_to: task.assigned_to || ''
    });
    setTaskNotes([]);
    setNewNote('');
    fetchTaskNotes(task.id);
    setShowTaskModal(true);
  };

  // ---- Aufgaben-Notizen ----
  const fetchTaskNotes = async (taskId) => {
    try {
      const data = await apiCall(`/admin/tasks/${taskId}/notes`);
      setTaskNotes(Array.isArray(data) ? data : []);
    } catch (error) {
      setTaskNotes([]);
    }
  };

  const handleAddNote = async () => {
    if (!editingTask || !newNote.trim()) return;
    try {
      const author = `${user?.firstname || ''} ${user?.lastname || ''}`.trim() || 'Leitung';
      await apiCall(`/admin/tasks/${editingTask.id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: newNote.trim(), author })
      });
      setNewNote('');
      fetchTaskNotes(editingTask.id);
      fetchTasks(); // 📝-Zähler auf der Karte aktualisieren
    } catch (error) {
      setMessage('Notiz konnte nicht gespeichert werden');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await apiCall(`/admin/task-notes/${noteId}`, { method: 'DELETE' });
      if (editingTask) fetchTaskNotes(editingTask.id);
      fetchTasks();
    } catch (error) {
      setMessage('Notiz konnte nicht gelöscht werden');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!await confirmDialog('Möchten Sie diese Aufgabe wirklich löschen?')) return;

    try {
      await apiCall(`/admin/tasks/${taskId}`, { method: 'DELETE' });
      setMessage('Aufgabe gelöscht');
      fetchTasks();
    } catch (error) {
      console.error('Fehler beim Löschen der Aufgabe:', error);
      setMessage('Fehler beim Löschen der Aufgabe');
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      // Hole die aktuelle Aufgabe, um alle Felder zu haben
      const currentTask = tasks.find(task => task.id === taskId);
      if (!currentTask) {
        setMessage('Aufgabe nicht gefunden');
        return;
      }

      // Optimistisches Update, damit die Karte beim Drag & Drop sofort umzieht
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t)));

      await apiCall(`/admin/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: currentTask.title,
          description: currentTask.description,
          category_id: currentTask.category_id,
          priority: currentTask.priority,
          status: newStatus,
          assigned_to: currentTask.assigned_to,
          assigned_by: currentTask.assigned_by,
          due_date: currentTask.due_date
        })
      });
      fetchTasks();
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Status:', error);
      setMessage('Fehler beim Aktualisieren des Status');
    }
  };

  const handleUploadDocument = async () => {
    try {
      if (!uploadFile) {
        setMessage('Bitte wählen Sie eine Datei aus');
        return;
      }

      const formData = new FormData();
      formData.append('document', uploadFile); // Geändert von 'file' zu 'document'
      formData.append('name', uploadFile.name);
      formData.append('category', docForm.category);
      formData.append('description', docForm.description);
      if (docForm.folder_id) {
        formData.append('folder_id', docForm.folder_id);
      }

      await apiCall('/admin/internal-documents', {
        method: 'POST',
        body: formData
      });

      setMessage('Dokument hochgeladen');
      setShowDocModal(false);
      setUploadFile(null);
      setDocForm({ category: '', folder_id: '', description: '' });
      fetchInternalDocuments();
    } catch (error) {
      console.error('Fehler beim Hochladen des Dokuments:', error);
      setMessage('Fehler beim Hochladen des Dokuments');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!await confirmDialog('Möchten Sie dieses Dokument wirklich löschen?')) return;

    try {
      await apiCall(`/admin/internal-documents/${docId}`, { method: 'DELETE' });
      setMessage('Dokument gelöscht');
      fetchInternalDocuments();
    } catch (error) {
      console.error('Fehler beim Löschen des Dokuments:', error);
      setMessage('Fehler beim Löschen des Dokuments');
    }
  };

  // Drag & Drop Handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setDraggedFiles(files);
      
      // Wenn nur eine Datei, direkt hochladen mit Standard-Einstellungen
      if (files.length === 1) {
        handleQuickUpload(files[0]);
      } else {
        // Bei mehreren Dateien, Modal öffnen für weitere Konfiguration
        setUploadFile(files[0]); // Erste Datei als Standard
        setShowDocModal(true);
      }
    }
  };

  const handleQuickUpload = async (file) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('document', file);
      
      // Standard-Kategorie verwenden (erste verfügbare)
      const defaultCategory = documentCategories.length > 0 ? documentCategories[0].name : '';
      const defaultFolder = selectedFolder?.id || '';
      
      formData.append('category', defaultCategory);
      formData.append('folder_id', defaultFolder);
      formData.append('description', `Hochgeladen via Drag & Drop: ${file.name}`);

      await apiCall('/admin/internal-documents', {
        method: 'POST',
        body: formData
      });

      setMessage(`Dokument "${file.name}" erfolgreich hochgeladen`);
      fetchInternalDocuments();
    } catch (error) {
      console.error('Fehler beim Drag & Drop Upload:', error);
      setMessage(`Fehler beim Hochladen von "${file.name}"`);
    } finally {
      setLoading(false);
      setDraggedFiles([]);
    }
  };

  const handleBulkUpload = async () => {
    if (draggedFiles.length === 0) return;

    try {
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;

      for (const file of draggedFiles) {
        try {
          const formData = new FormData();
          formData.append('document', file);
          formData.append('category', docForm.category);
          formData.append('folder_id', docForm.folder_id);
          formData.append('description', docForm.description || `Bulk Upload: ${file.name}`);

          await apiCall('/admin/internal-documents', {
            method: 'POST',
            body: formData
          });
          successCount++;
        } catch (error) {
          console.error(`Fehler beim Upload von ${file.name}:`, error);
          errorCount++;
        }
      }

      setMessage(`${successCount} Dokument(e) erfolgreich hochgeladen${errorCount > 0 ? `, ${errorCount} Fehler` : ''}`);
      setShowDocModal(false);
      setDraggedFiles([]);
      setUploadFile(null);
      setDocForm({ category: '', folder_id: '', description: '' });
      fetchInternalDocuments();
    } catch (error) {
      console.error('Fehler beim Bulk Upload:', error);
      setMessage('Fehler beim Bulk Upload');
    } finally {
      setLoading(false);
    }
  };

  // Search Functions
  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    try {
      setIsSearching(true);
      const data = await apiCall(`/api/admin/documents/search?q=${encodeURIComponent(query)}`);
      setSearchResults(data || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Fehler bei der Suche:', error);
      setMessage('Fehler bei der Suche');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Debounce search - only search after user stops typing for 500ms
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      handleSearch(query);
    }, 500);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  };

  // ==============================================
  // KATEGORIE-MANAGEMENT FUNKTIONEN
  // ==============================================

  const handleSaveCategory = async () => {
    try {
      if (!categoryForm.name.trim()) {
        setMessage('Bitte geben Sie einen Namen ein');
        return;
      }

      const method = editingCategory ? 'PUT' : 'POST';
      const url = editingCategory 
        ? `/admin/document-categories/${editingCategory.id}` 
        : '/admin/document-categories';

      await apiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryForm)
      });

      setMessage(editingCategory ? 'Kategorie aktualisiert' : 'Kategorie erstellt');
      setShowCategoryModal(false);
      resetCategoryForm();
      fetchDocumentCategories();
    } catch (error) {
      console.error('Fehler beim Speichern der Kategorie:', error);
      setMessage('Fehler beim Speichern der Kategorie');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      color: category.color || '#007bff',
      icon: category.icon || 'bi-folder'
    });
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!await confirmDialog('Sind Sie sicher, dass Sie diese Kategorie löschen möchten?')) {
      return;
    }

    try {
      await apiCall(`/admin/document-categories/${categoryId}`, {
        method: 'DELETE'
      });
      setMessage('Kategorie gelöscht');
      fetchDocumentCategories();
    } catch (error) {
      console.error('Fehler beim Löschen der Kategorie:', error);
      setMessage(error.message || 'Fehler beim Löschen der Kategorie');
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      description: '',
      color: '#007bff',
      icon: 'bi-folder'
    });
    setEditingCategory(null);
  };

  const resetFolderForm = () => {
    setFolderForm({
      name: '',
      description: '',
      parent_id: '',
      category_id: '',
      color: '#007bff',
      icon: 'bi-folder'
    });
    setEditingFolder(null);
  };

  const handleSaveFolder = async () => {
    try {
      const url = editingFolder 
        ? `/admin/document-folders/${editingFolder.id}` 
        : '/admin/document-folders';
      
      await apiCall(url, {
        method: editingFolder ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(folderForm)
      });

      setMessage(editingFolder ? 'Ordner aktualisiert' : 'Ordner erstellt');
      setShowFolderModal(false);
      resetFolderForm();
      if (folderForm.category_id) {
        fetchDocumentFolders(folderForm.category_id);
      }
    } catch (error) {
      console.error('Fehler beim Speichern des Ordners:', error);
      setMessage('Fehler beim Speichern des Ordners');
    }
  };

  const handleEditFolder = (folder) => {
    setFolderForm({
      name: folder.name,
      description: folder.description || '',
      parent_id: folder.parent_id || '',
      category_id: folder.category_id,
      color: folder.color || '#007bff',
      icon: folder.icon || 'bi-folder'
    });
    setEditingFolder(folder);
    setShowFolderModal(true);
  };

  const handleDeleteFolder = async (folderId) => {
    if (!await confirmDialog('Möchten Sie diesen Ordner wirklich löschen?')) return;

    try {
      await apiCall(`/admin/document-folders/${folderId}`, {
        method: 'DELETE'
      });
      setMessage('Ordner gelöscht');
      if (selectedFolder && documentCategories.length > 0) {
        fetchDocumentFolders(documentCategories[0].id);
      }
    } catch (error) {
      console.error('Fehler beim Löschen des Ordners:', error);
      setMessage('Fehler beim Löschen des Ordners');
    }
  };

  // Task Kategorien Management
  const resetTaskCategoryForm = () => {
    setTaskCategoryForm({
      name: '',
      description: '',
      color: '#007bff',
      icon: 'bi-list-task'
    });
    setEditingTaskCategory(null);
  };

  const handleSaveTaskCategory = async () => {
    try {
      const url = editingTaskCategory 
        ? `/admin/task-categories/${editingTaskCategory.id}` 
        : '/admin/task-categories';
      
      await apiCall(url, {
        method: editingTaskCategory ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(taskCategoryForm)
      });

      setMessage(editingTaskCategory ? 'Task-Kategorie aktualisiert' : 'Task-Kategorie erstellt');
      setShowTaskCategoryModal(false);
      resetTaskCategoryForm();
      fetchTaskCategories();
    } catch (error) {
      console.error('Fehler beim Speichern der Task-Kategorie:', error);
      setMessage('Fehler beim Speichern der Task-Kategorie');
    }
  };

  const handleEditTaskCategory = (category) => {
    setTaskCategoryForm({
      name: category.name,
      description: category.description || '',
      color: category.color || '#007bff',
      icon: category.icon || 'bi-list-task'
    });
    setEditingTaskCategory(category);
    setShowTaskCategoryModal(true);
  };

  const handleDeleteTaskCategory = async (categoryId) => {
    if (!await confirmDialog('Möchten Sie diese Task-Kategorie wirklich löschen?')) return;

    try {
      await apiCall(`/admin/task-categories/${categoryId}`, {
        method: 'DELETE'
      });
      setMessage('Task-Kategorie gelöscht');
      fetchTaskCategories();
    } catch (error) {
      console.error('Fehler beim Löschen der Task-Kategorie:', error);
      setMessage('Fehler beim Löschen der Task-Kategorie');
    }
  };

  // Aufgaben Reset Funktionen
  // Backend erwartet categories als 'all' oder Array von IDs — das Select liefert einen String
  const buildResetOptions = () => ({
    ...resetForm,
    categories: resetForm.categories === 'all' ? 'all' : [parseInt(resetForm.categories)]
  });

  const handleResetPreview = async () => {
    try {
      const data = await apiCall('/admin/tasks/reset-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          year: resetForm.year,
          resetOptions: buildResetOptions()
        })
      });
      setResetPreview(data);
    } catch (error) {
      console.error('Fehler beim Erstellen der Reset-Vorschau:', error);
      setResetPreview(null);
      setMessage('Fehler beim Erstellen der Reset-Vorschau');
    }
  };

  // Vorschau automatisch laden, wenn das Modal geöffnet oder eine Option geändert wird
  useEffect(() => {
    if (showResetModal && resetForm.year) {
      handleResetPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResetModal, resetForm]);

  const handleResetTasks = async () => {
    if (!await confirmDialog(`Möchten Sie wirklich alle ausgewählten Aufgaben für ${resetForm.year} zurücksetzen?`)) return;

    try {
      const data = await apiCall('/admin/tasks/reset-year', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          year: resetForm.year,
          resetOptions: buildResetOptions()
        })
      });
      
      setMessage(`${data.totalAffected} Aufgaben erfolgreich für ${resetForm.year} zurückgesetzt`);
      setShowResetModal(false);
      setResetPreview(null);
      fetchTasks();
    } catch (error) {
      console.error('Fehler beim Zurücksetzen der Aufgaben:', error);
      setMessage('Fehler beim Zurücksetzen der Aufgaben');
    }
  };

  // Alias für handleResetTasks für einheitliche Namensgebung
  const handleExecuteReset = handleResetTasks;

  // Material Management Funktionen
  const handleSaveMaterial = async () => {
    try {
      const method = editingMaterial ? 'PUT' : 'POST';
      const url = editingMaterial ? `/admin/materials/${editingMaterial.id}` : '/admin/materials';
      
      await apiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(materialForm)
      });
      
      setMessage(editingMaterial ? 'Material erfolgreich aktualisiert' : 'Material erfolgreich erstellt');
      setShowMaterialModal(false);
      resetMaterialForm();
      fetchMaterials();
    } catch (error) {
      console.error('Fehler beim Speichern des Materials:', error);
      setMessage('Fehler beim Speichern des Materials');
    }
  };

  const handleEditMaterial = (material) => {
    setEditingMaterial(material);
    setMaterialForm({
      name: material.name || '',
      description: material.description || '',
      category_id: material.category_id || '',
      type: material.type || 'consumable',
      unit: material.unit || '',
      current_stock: material.current_stock || '',
      min_stock: material.min_stock || '',
      max_stock: material.max_stock || '',
      cost_per_unit: material.cost_per_unit || '',
      supplier: material.supplier || '',
      location: material.location || '',
      notes: material.notes || ''
    });
    setShowMaterialModal(true);
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!await confirmDialog('Möchten Sie dieses Material wirklich löschen?')) return;
    
    try {
      await apiCall(`/admin/materials/${materialId}`, { method: 'DELETE' });
      setMessage('Material erfolgreich gelöscht');
      fetchMaterials();
    } catch (error) {
      console.error('Fehler beim Löschen des Materials:', error);
      setMessage('Fehler beim Löschen des Materials');
    }
  };

  const handleUpdateStock = async (materialId, newStock) => {
    try {
      await apiCall(`/admin/materials/${materialId}/stock`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          current_stock: newStock,
          last_counted: new Date().toISOString().split('T')[0]
        })
      });
      setMessage('Bestand erfolgreich aktualisiert');
      fetchMaterials();
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Bestands:', error);
      setMessage('Fehler beim Aktualisieren des Bestands');
    }
  };

  const resetMaterialForm = () => {
    setMaterialForm({
      name: '',
      description: '',
      category_id: materialCategories.length > 0 ? materialCategories[0].id : '',
      type: 'consumable',
      unit: '',
      current_stock: '',
      min_stock: '',
      max_stock: '',
      cost_per_unit: '',
      supplier: '',
      location: '',
      notes: ''
    });
    setEditingMaterial(null);
  };

  // Material Category Management Funktionen
  const handleSaveMaterialCategory = async () => {
    try {
      const isEditing = editingMaterialCategory && editingMaterialCategory.id !== 'new';
      const method = isEditing ? 'PUT' : 'POST';
      const url = isEditing ? `/admin/material-categories/${editingMaterialCategory.id}` : '/admin/material-categories';
      
      await apiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(materialCategoryForm)
      });
      
      setMessage(isEditing ? 'Material-Kategorie erfolgreich aktualisiert' : 'Material-Kategorie erfolgreich erstellt');
      setShowMaterialCategoryModal(false);
      resetMaterialCategoryForm();
      fetchMaterialCategories();
      fetchMaterials(); // Refresh materials to update category info
    } catch (error) {
      console.error('Fehler beim Speichern der Material-Kategorie:', error);
      setMessage('Fehler beim Speichern der Material-Kategorie');
    }
  };

  const handleEditMaterialCategory = (category) => {
    setEditingMaterialCategory(category);
    setMaterialCategoryForm({
      name: category.name || '',
      description: category.description || '',
      type: category.type || 'consumable',
      color: category.color || '#007bff',
      icon: category.icon || 'bi-box'
    });
    setShowMaterialCategoryModal(true);
  };

  const handleDeleteMaterialCategory = async (categoryId) => {
    if (!await confirmDialog('Möchten Sie diese Material-Kategorie wirklich löschen?')) return;
    
    try {
      await apiCall(`/admin/material-categories/${categoryId}`, { method: 'DELETE' });
      setMessage('Material-Kategorie erfolgreich gelöscht');
      fetchMaterialCategories();
      fetchMaterials(); // Refresh materials
    } catch (error) {
      console.error('Fehler beim Löschen der Material-Kategorie:', error);
      setMessage('Fehler beim Löschen der Material-Kategorie');
    }
  };

  const resetMaterialCategoryForm = () => {
    setMaterialCategoryForm({
      name: '',
      description: '',
      type: 'consumable',
      color: '#007bff',
      icon: 'bi-box'
    });
    setEditingMaterialCategory(null);
  };

  // Expense Management Functions
  const handleSaveExpense = async () => {
    try {
      if (!expenseForm.from_person.trim() || !expenseForm.reason.trim() || !expenseForm.date) {
        setMessage('Bitte füllen Sie alle erforderlichen Felder aus');
        return;
      }

      const method = editingExpense ? 'PUT' : 'POST';
      const url = editingExpense ? `/admin/expenses/${editingExpense.id}` : '/admin/expenses';
      
      await apiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(expenseForm)
      });
      
      setMessage(editingExpense ? 'Auslage erfolgreich aktualisiert' : 'Auslage erfolgreich erstellt');
      setShowExpenseModal(false);
      resetExpenseForm();
      fetchExpenses();
    } catch (error) {
      console.error('Fehler beim Speichern der Auslage:', error);
      setMessage('Fehler beim Speichern der Auslage');
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      from_person: expense.from_person || '',
      reason: expense.reason || '',
      date: expense.date ? expense.date.split('T')[0] : '',
      amount: expense.amount || '',
      completed: expense.completed || false
    });
    setShowExpenseModal(true);
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!await confirmDialog('Möchten Sie diese Auslage wirklich löschen?')) return;
    
    try {
      await apiCall(`/admin/expenses/${expenseId}`, { method: 'DELETE' });
      setMessage('Auslage erfolgreich gelöscht');
      fetchExpenses();
    } catch (error) {
      console.error('Fehler beim Löschen der Auslage:', error);
      setMessage('Fehler beim Löschen der Auslage');
    }
  };

  const handleToggleExpenseStatus = async (expenseId, currentStatus) => {
    try {
      await apiCall(`/admin/expenses/${expenseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed: !currentStatus })
      });
      fetchExpenses();
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Status:', error);
      setMessage('Fehler beim Aktualisieren des Status');
    }
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      from_person: '',
      reason: '',
      date: '',
      amount: '',
      completed: false
    });
    setEditingExpense(null);
  };

  // Fence Management Functions
  const handleSaveFence = async () => {
    try {
      if (!fenceForm.station || !fenceForm.number_from.trim()) {
        setMessage('Bitte füllen Sie alle erforderlichen Felder aus');
        return;
      }

      const method = editingFence ? 'PUT' : 'POST';
      const url = editingFence ? `/admin/fences/${editingFence.id}` : '/admin/fences';
      
      await apiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fenceForm)
      });
      
      setMessage(editingFence ? 'Bauzaun erfolgreich aktualisiert' : 'Bauzaun erfolgreich erstellt');
      setShowFenceModal(false);
      resetFenceForm();
      // fetchFences(); // Entfernt
    } catch (error) {
      console.error('Fehler beim Speichern des Bauzauns:', error);
      setMessage('Fehler beim Speichern des Bauzauns');
    }
  };

  const handleEditFence = (fence) => {
    setEditingFence(fence);
    setFenceForm(fence);
    setShowFenceModal(true);
  };

  const handleDeleteFence = async (fenceId) => {
    if (!await confirmDialog('Möchten Sie diesen Bauzaun wirklich löschen?')) return;
    
    try {
      await apiCall(`/admin/fences/${fenceId}`, { method: 'DELETE' });
      setMessage('Bauzaun erfolgreich gelöscht');
      // fetchFences(); // Entfernt
    } catch (error) {
      console.error('Fehler beim Löschen des Bauzauns:', error);
      setMessage('Fehler beim Löschen des Bauzauns');
    }
  };

  const handleUpdateFence = async (fence) => {
    try {
      await apiCall(`/admin/fences/${fence.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fence)
      });
      setMessage('Bauzaun erfolgreich aktualisiert');
      // fetchFences(); // Entfernt
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Bauzauns:', error);
      setMessage('Fehler beim Aktualisieren des Bauzauns');
    }
  };

  const resetFenceForm = () => {
    setFenceForm({
      station: selectedStation,
      number_from: '',
      number_to: '',
      x: 0,
      y: 0,
      width: 100,
      height: 30,
      is_short: false,
      color: '#ffc107'
    });
    setEditingFence(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  // Datum-genau (ohne Uhrzeit): heute fällig ist noch NICHT überfällig
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(String(dueDate).slice(0, 10)) < today;
  };

  if (user?.role !== 'admin') {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Nur Administratoren haben Zugriff auf Aufgaben und Planung.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      {/* Styles für Task-Cards und mobile Optimierung */}
      <style>{`
        .btn-xs {
          font-size: 0.75rem;
          padding: 2px 6px;
          line-height: 1.2;
          min-width: auto;
        }
        .gap-1 {
          gap: 0.25rem !important;
        }
        @media (max-width: 768px) {
          .btn-xs {
            font-size: 0.7rem;
            padding: 1px 4px;
          }
          .btn-sm {
            font-size: 0.8rem;
            padding: 0.25rem 0.5rem;
          }
          .card-body.p-2 {
            padding: 0.5rem !important;
          }
        }
      `}</style>
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="bi bi-list-task me-2 text-primary"></i>
              Aufgaben und Planung
            </h2>
          </div>

          {message && (
            <div className="alert alert-info alert-dismissible fade show" role="alert">
              {message}
              <button type="button" className="btn-close" onClick={() => setMessage('')}></button>
            </div>
          )}

          {/* Tab Navigation */}
          <ul className="nav nav-tabs mb-4">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'tasks' ? 'active' : ''}`}
                onClick={() => setActiveTab('tasks')}
              >
                <i className="bi bi-check-square me-2"></i>
                Aufgaben & Abläufe
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'documents' ? 'active' : ''}`}
                onClick={() => setActiveTab('documents')}
              >
                <i className="bi bi-folder-fill me-2"></i>
                Interne Dokumente
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'materials' ? 'active' : ''}`}
                onClick={() => setActiveTab('materials')}
              >
                <i className="bi bi-box-seam me-2"></i>
                Material
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'expenses' ? 'active' : ''}`}
                onClick={() => setActiveTab('expenses')}
              >
                <i className="bi bi-wallet2 me-2"></i>
                Auslagen
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'signage' ? 'active' : ''}`}
                onClick={() => setActiveTab('signage')}
              >
                <i className="bi bi-sign-turn-right me-2"></i>
                Beschilderung
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'station-planner' ? 'active' : ''}`}
                onClick={() => setActiveTab('station-planner')}
              >
                <i className="bi bi-grid-3x3 me-2"></i>
                Station Planer
              </button>
            </li>
            
            {/* Einstellungen Dropdown */}
            <li className="nav-item dropdown position-relative">
              <button
                className={`nav-link dropdown-toggle ${['folders', 'categories', 'task-categories', 'reset'].includes(activeTab) ? 'active' : ''}`}
                type="button"
                onMouseEnter={() => setShowSettingsDropdown(true)}
                onMouseLeave={() => setShowSettingsDropdown(false)}
              >
                <i className="bi bi-gear-fill me-2"></i>
                Einstellungen
              </button>
              {showSettingsDropdown && (
                <ul className="dropdown-menu show position-absolute"
                    style={{ top: '100%', left: 0, zIndex: 1050 }}
                    onMouseEnter={() => setShowSettingsDropdown(true)}
                    onMouseLeave={() => setShowSettingsDropdown(false)}>
                  <li>
                    <button
                      className={`dropdown-item ${activeTab === 'folders' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab('folders');
                        setShowSettingsDropdown(false);
                      }}
                    >
                      <i className="bi bi-folder-tree me-2"></i>
                      Ordner verwalten
                    </button>
                  </li>
                  <li>
                    <button
                      className={`dropdown-item ${activeTab === 'categories' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab('categories');
                        setShowSettingsDropdown(false);
                      }}
                    >
                      <i className="bi bi-folder me-2"></i>
                      Dokument-Kategorien
                    </button>
                  </li>
                  <li>
                    <button
                      className={`dropdown-item ${activeTab === 'task-categories' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab('task-categories');
                        setShowSettingsDropdown(false);
                      }}
                    >
                      <i className="bi bi-tags-fill me-2"></i>
                      Task-Kategorien
                    </button>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button
                      className={`dropdown-item ${activeTab === 'reset' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab('reset');
                        setShowSettingsDropdown(false);
                      }}
                    >
                      <i className="bi bi-arrow-clockwise me-2"></i>
                      Jahres-Reset
                    </button>
                  </li>
                </ul>
              )}
            </li>
          </ul>

          {/* Tasks Tab */}
          {activeTab === 'tasks' && (() => {
            // Filter + Sortierung: überfällige zuerst, dann nach Fälligkeit, dann Priorität
            const q = taskSearch.trim().toLowerCase();
            const visibleTasks = tasks.filter((t) =>
              (taskFilterCat === 'all' || String(t.category_id) === String(taskFilterCat)) &&
              (taskFilterPrio === 'all' || t.priority === taskFilterPrio) &&
              (!q || `${t.title} ${t.description || ''} ${t.assigned_to_name || ''}`.toLowerCase().includes(q))
            );
            const sortTasks = (arr) => [...arr].sort((a, b) => {
              const ao = isOverdue(a.due_date) ? 0 : 1;
              const bo = isOverdue(b.due_date) ? 0 : 1;
              if (ao !== bo) return ao - bo;
              const ad = a.due_date ? String(a.due_date).slice(0, 10) : '9999';
              const bd = b.due_date ? String(b.due_date).slice(0, 10) : '9999';
              if (ad !== bd) return ad < bd ? -1 : 1;
              return (prioWeight[a.priority] ?? 2) - (prioWeight[b.priority] ?? 2);
            });
            const filterActive = q || taskFilterCat !== 'all' || taskFilterPrio !== 'all';
            const statusOrder = Object.keys(statuses);

            const renderTaskCard = (task) => {
              const cat = taskCategories.find((c) => c.id === task.category_id);
              const overdue = isOverdue(task.due_date) && !['completed', 'cancelled'].includes(task.status);
              const toInitials = (name) => name
                ? name.split(' ').filter(Boolean).map((p) => p[0]).join('').slice(0, 2).toUpperCase()
                : null;
              const initials = toInitials(task.assigned_to_name);
              const initials2 = toInitials(task.assigned_to2_name);
              const idx = statusOrder.indexOf(task.status);
              return (
                <div
                  key={task.id}
                  className={`card mb-2 task-card ${isMobile ? 'mobile' : ''}`}
                  draggable={!isMobile}
                  onDragStart={(e) => { setDragTaskId(task.id); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragEnd={() => { setDragTaskId(null); setDragOverStatus(null); }}
                  onClick={() => handleEditTask(task)}
                  style={{
                    borderLeft: `4px solid ${prioColors[task.priority] || '#adb5bd'}`,
                    opacity: dragTaskId === task.id ? 0.4 : 1,
                    position: 'relative'
                  }}
                >
                  <div className="card-body p-2">
                    <div className="fw-semibold" style={{ fontSize: '0.85rem', lineHeight: 1.25, paddingRight: '3rem' }}>
                      {task.title}
                    </div>
                    <div className="task-actions position-absolute d-flex gap-1" style={{ top: '0.35rem', right: '0.35rem' }}>
                      <button
                        className="btn btn-sm btn-light border py-0 px-1"
                        onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}
                        title="Bearbeiten"
                      >
                        <i className="bi bi-pencil" style={{ fontSize: '0.7rem' }}></i>
                      </button>
                      <button
                        className="btn btn-sm btn-light border py-0 px-1 text-danger"
                        onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
                        title="Löschen"
                      >
                        <i className="bi bi-trash" style={{ fontSize: '0.7rem' }}></i>
                      </button>
                    </div>
                    {task.description && (
                      <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                        {task.description.length > 80 ? task.description.substring(0, 80) + '…' : task.description}
                      </div>
                    )}
                    <div className="d-flex align-items-center flex-wrap gap-1 mt-1">
                      <span className="text-muted" style={{ fontSize: '0.65rem' }}>#{task.id}</span>
                      {cat && (
                        <span
                          className="badge"
                          style={{ background: `${cat.color || '#6c757d'}22`, color: cat.color || '#6c757d', fontSize: '0.65rem', fontWeight: 600 }}
                        >
                          {cat.name}
                        </span>
                      )}
                      {task.due_date && (
                        <span className={`badge ${overdue ? 'bg-danger' : 'bg-light text-dark border'}`} style={{ fontSize: '0.65rem' }}>
                          {overdue ? '⚠️ ' : '📅 '}{formatDate(task.due_date)}
                        </span>
                      )}
                      {task.note_count > 0 && (
                        <span className="badge bg-light text-dark border" style={{ fontSize: '0.65rem' }} title={`${task.note_count} Notizen`}>
                          📝 {task.note_count}
                        </span>
                      )}
                      {initials && (
                        <span
                          className="badge rounded-pill bg-primary ms-auto"
                          title={`Zuständig: ${task.assigned_to_name}`}
                          style={{ fontSize: '0.65rem' }}
                        >
                          {initials}
                        </span>
                      )}
                      {initials2 && (
                        <span
                          className={`badge rounded-pill bg-info ${initials ? '' : 'ms-auto'}`}
                          title={`Zuständig: ${task.assigned_to2_name}`}
                          style={{ fontSize: '0.65rem' }}
                        >
                          {initials2}
                        </span>
                      )}
                    </div>
                    {isMobile && (
                      <div className="d-flex gap-1 mt-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn btn-sm btn-outline-secondary py-0 flex-fill"
                          style={{ fontSize: '0.7rem' }}
                          disabled={idx <= 0}
                          onClick={() => handleUpdateTaskStatus(task.id, statusOrder[idx - 1])}
                        >
                          ◀ {idx > 0 ? statuses[statusOrder[idx - 1]].label : '–'}
                        </button>
                        <button
                          className="btn btn-sm btn-outline-primary py-0 flex-fill"
                          style={{ fontSize: '0.7rem' }}
                          disabled={idx >= statusOrder.length - 1}
                          onClick={() => handleUpdateTaskStatus(task.id, statusOrder[idx + 1])}
                        >
                          {idx < statusOrder.length - 1 ? statuses[statusOrder[idx + 1]].label : '–'} ▶
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            };

            return (
              <div>
                <style>{`
                  .task-card { transition: box-shadow .15s ease, opacity .15s ease; cursor: grab; }
                  .task-card:hover { box-shadow: 0 2px 10px rgba(0,0,0,.14); }
                  .task-card .task-actions { opacity: 0; transition: opacity .15s ease; }
                  .task-card:hover .task-actions, .task-card.mobile .task-actions { opacity: 1; }
                  .task-col.drag-over { background: #e7f1ff; outline: 2px dashed #0d6efd; outline-offset: -2px; }
                  .task-board { display: flex; gap: .8rem; overflow-x: auto; align-items: flex-start; padding-bottom: .5rem; }
                  .task-board > div { flex: 1 0 235px; min-width: 235px; }
                  .status-tab-row { display: flex; gap: .4rem; overflow-x: auto; padding-bottom: .3rem; }
                  .status-tab-row .btn { white-space: nowrap; }
                `}</style>

                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <h4 className="mb-0">Aufgaben & Abläufe verwalten</h4>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setEditingTask(null);
                      setTaskForm({
                        title: '',
                        description: '',
                        priority: 'medium',
                        status: 'todo',
                        due_date: '',
                        category_id: taskCategories.length > 0 ? taskCategories[0].id : '',
                        assigned_to: ''
                      });
                      setTaskNotes([]);
                      setNewNote('');
                      setShowTaskModal(true);
                    }}
                  >
                    <i className="bi bi-plus-lg me-2"></i>
                    Neue Aufgabe
                  </button>
                </div>

                {/* Filterleiste */}
                <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                  <div className="input-group input-group-sm" style={{ maxWidth: '240px' }}>
                    <span className="input-group-text"><i className="bi bi-search"></i></span>
                    <input
                      className="form-control"
                      placeholder="Aufgaben durchsuchen…"
                      value={taskSearch}
                      onChange={(e) => setTaskSearch(e.target.value)}
                    />
                    {taskSearch && (
                      <button className="btn btn-outline-secondary" onClick={() => setTaskSearch('')}>×</button>
                    )}
                  </div>
                  <select
                    className="form-select form-select-sm"
                    style={{ maxWidth: '180px' }}
                    value={taskFilterCat}
                    onChange={(e) => setTaskFilterCat(e.target.value)}
                  >
                    <option value="all">Alle Kategorien</option>
                    {taskCategories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <select
                    className="form-select form-select-sm"
                    style={{ maxWidth: '170px' }}
                    value={taskFilterPrio}
                    onChange={(e) => setTaskFilterPrio(e.target.value)}
                  >
                    <option value="all">Alle Prioritäten</option>
                    {Object.entries(priorities).map(([key, info]) => (
                      <option key={key} value={key}>{info.icon} {info.label}</option>
                    ))}
                  </select>
                  {filterActive && (
                    <span className="text-muted small">
                      {visibleTasks.length} von {tasks.length} Aufgaben
                      <button
                        className="btn btn-link btn-sm p-0 ms-2"
                        onClick={() => { setTaskSearch(''); setTaskFilterCat('all'); setTaskFilterPrio('all'); }}
                      >
                        zurücksetzen
                      </button>
                    </span>
                  )}
                </div>

                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Lädt...</span>
                    </div>
                  </div>
                ) : isMobile ? (
                  /* Handy: Status-Tabs statt Spalten, Verschieben per Knopf */
                  <div>
                    <div className="status-tab-row mb-2">
                      {statusOrder.map((k) => (
                        <button
                          key={k}
                          className={`btn btn-sm ${mobileStatus === k ? `btn-${statuses[k].class} ${statuses[k].class === 'warning' ? '' : 'text-white'}` : 'btn-outline-secondary'}`}
                          onClick={() => setMobileStatus(k)}
                        >
                          {statuses[k].icon} {statuses[k].label} ({visibleTasks.filter((t) => t.status === k).length})
                        </button>
                      ))}
                    </div>
                    {sortTasks(visibleTasks.filter((t) => t.status === mobileStatus)).map(renderTaskCard)}
                    {visibleTasks.filter((t) => t.status === mobileStatus).length === 0 && (
                      <p className="text-muted text-center small py-4">Keine Aufgaben in „{statuses[mobileStatus].label}"</p>
                    )}
                  </div>
                ) : (
                  /* Desktop: Kanban mit Drag & Drop */
                  <div className="task-board">
                    {statusOrder.map((statusKey) => {
                      const statusInfo = statuses[statusKey];
                      const statusTasks = sortTasks(visibleTasks.filter((t) => t.status === statusKey));
                      return (
                        <div key={statusKey}>
                          <div
                            className={`card task-col ${dragOverStatus === statusKey ? 'drag-over' : ''}`}
                            onDragOver={(e) => { if (dragTaskId) { e.preventDefault(); if (dragOverStatus !== statusKey) setDragOverStatus(statusKey); } }}
                            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverStatus(null); }}
                            onDrop={(e) => {
                              e.preventDefault();
                              if (dragTaskId) handleUpdateTaskStatus(dragTaskId, statusKey);
                              setDragTaskId(null);
                              setDragOverStatus(null);
                            }}
                          >
                            <div className={`card-header bg-${statusInfo.class} text-white py-2`}>
                              <h6 className="mb-0" style={{ fontSize: '0.85rem' }}>
                                {statusInfo.icon} {statusInfo.label} ({statusTasks.length})
                              </h6>
                            </div>
                            <div className="card-body p-2" style={{ minHeight: '140px' }}>
                              {statusTasks.map(renderTaskCard)}
                              {statusTasks.length === 0 && (
                                <p className="text-muted text-center small mb-0 py-4">
                                  {dragTaskId ? '⬇ Hier ablegen' : 'Keine Aufgaben'}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Interne Dokumente verwalten</h4>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowDocModal(true)}
                >
                  <i className="bi bi-upload me-2"></i>
                  Dokument hochladen
                </button>
              </div>

              {/* Drag & Drop Zone */}
              <div 
                className={`card mb-4 drag-drop-zone ${isDragOver ? 'drag-over' : 'border-dashed'}`}
                style={{
                  border: isDragOver ? '2px solid #0d6efd' : '2px dashed #dee2e6',
                  borderRadius: '10px',
                  transition: 'all 0.3s ease'
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => setShowDocModal(true)}
              >
                <div className="card-body text-center py-5">
                  {isDragOver ? (
                    <>
                      <i className="bi bi-cloud-arrow-up display-1 text-primary"></i>
                      <h5 className="text-primary mt-3">Loslassen zum Hochladen</h5>
                      <p className="text-muted">Dateien werden automatisch hochgeladen</p>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-cloud-upload display-1 text-muted"></i>
                      <h5 className="text-muted mt-3">Dateien hier hineinziehen</h5>
                      <p className="text-muted">
                        Ziehen Sie Dateien hierher oder{' '}
                        <button 
                          className="btn btn-link p-0 text-decoration-underline"
                          onClick={() => setShowDocModal(true)}
                        >
                          klicken Sie hier
                        </button>{' '}
                        zum Hochladen
                      </p>
                      <small className="text-muted">
                        Unterstützte Formate: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, etc.
                      </small>
                    </>
                  )}
                </div>
              </div>

              {/* Search Bar */}
              <div className="card mb-4">
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Dokumente durchsuchen (Dateiname, Beschreibung, Inhalt...)"
                          value={searchQuery}
                          onChange={handleSearchInputChange}
                        />
                        {searchQuery && (
                          <button 
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={clearSearch}
                          >
                            <i className="bi bi-x"></i>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="col-md-4">
                      {isSearching && (
                        <div className="d-flex align-items-center text-muted">
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          Suche läuft...
                        </div>
                      )}
                      {showSearchResults && !isSearching && (
                        <span className="text-muted">
                          {searchResults.length} Ergebnis(se) gefunden
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Search Results */}
              {showSearchResults && (
                <div className="mb-4">
                  <h5 className="mb-3">
                    <i className="bi bi-search me-2"></i>
                    Suchergebnisse für "{searchQuery}"
                  </h5>
                  
                  {searchResults.length === 0 ? (
                    <div className="alert alert-info">
                      <i className="bi bi-info-circle me-2"></i>
                      Keine Dokumente gefunden, die Ihren Suchkriterien entsprechen.
                    </div>
                  ) : (
                    <div className="row">
                      {searchResults.map(doc => (
                        <div key={doc.id} className="col-md-6 col-lg-4 mb-3">
                          <div className="card h-100 border-primary">
                            <div className="card-header bg-primary bg-opacity-10">
                              <h6 className="card-title mb-0 d-flex align-items-center">
                                <i className="bi bi-file-earmark me-2 text-primary"></i>
                                <span dangerouslySetInnerHTML={{
                                  __html: highlightSearchTerm(doc.filename, searchQuery)
                                }}></span>
                              </h6>
                            </div>
                            <div className="card-body">
                              <p className="card-text small">
                                <strong>Kategorie:</strong> {doc.category}
                                {doc.folder_name && (
                                  <>
                                    <br />
                                    <strong>Ordner:</strong> {doc.folder_name}
                                  </>
                                )}
                              </p>
                              
                              {doc.description && (
                                <p className="card-text small text-muted">
                                  <strong>Beschreibung:</strong>{' '}
                                  <span dangerouslySetInnerHTML={{
                                    __html: highlightSearchTerm(doc.description, searchQuery)
                                  }}></span>
                                </p>
                              )}
                              
                              {doc.content_excerpt && (
                                <p className="card-text small text-success">
                                  <strong><i className="bi bi-file-text me-1"></i>Inhalt:</strong>{' '}
                                  <span dangerouslySetInnerHTML={{
                                    __html: highlightSearchTerm(doc.content_excerpt, searchQuery)
                                  }}></span>
                                  {doc.content_excerpt.length > 100 && '...'}
                                </p>
                              )}
                              
                              <div className="d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                  <i className="bi bi-calendar me-1"></i>
                                  {formatDate(doc.upload_date)}
                                </small>
                                <div className="d-flex gap-1">
                                  <a
                                    href={`/uploads/${doc.filename}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-primary"
                                    title="Öffnen"
                                  >
                                    <i className="bi bi-eye"></i>
                                  </a>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDeleteDocument(doc.id)}
                                    title="Löschen"
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Regular Document View (hidden when searching) */}
              {!showSearchResults && (
              <div className="row">
                {documentCategories.map(category => {
                  const categoryDocs = internalDocuments.filter(doc => doc.category === category.name);
                  return (
                    <div key={category.id} className="col-md-4 mb-4">
                      <div className="card h-100">
                        <div className="card-header" style={{ backgroundColor: category.color + '20' }}>
                          <h6 className="mb-0">
                            <i className={`${category.icon} me-2`} style={{ color: category.color }}></i>
                            {category.name} ({categoryDocs.length})
                          </h6>
                        </div>
                        <div className="card-body">
                          {categoryDocs.map(doc => (
                            <div key={doc.id} className="border-bottom pb-2 mb-2">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <h6 className="mb-0 flex-grow-1">{doc.name}</h6>
                                <small className="text-muted me-2">
                                  {formatDate(doc.uploaded_at)}
                                </small>
                                <div className="d-flex gap-1">
                                  <a
                                    href={withToken(`/api/admin/internal-documents/${doc.id}/download`)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-primary"
                                    title="Herunterladen"
                                  >
                                    <i className="bi bi-download"></i>
                                  </a>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDeleteDocument(doc.id)}
                                    title="Löschen"
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                              </div>
                              {doc.description && (
                                <p className="text-muted small mb-0">{doc.description}</p>
                              )}
                            </div>
                          ))}
                          {categoryDocs.length === 0 && (
                            <p className="text-muted text-center small">
                              Keine Dokumente
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          )}

          {/* Folders Management Tab */}
          {activeTab === 'folders' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">
                  <i className="bi bi-folder-tree me-2"></i>
                  Ordner verwalten
                </h4>
                <div>
                  {/* Kategorie-Auswahl für Ordner */}
                  <select 
                    className="form-select me-3 d-inline-block" 
                    style={{ width: 'auto' }}
                    value={selectedFolder ? selectedFolder.category_id : (documentCategories[0]?.id || '')}
                    onChange={(e) => {
                      const categoryId = e.target.value;
                      setSelectedFolder({ category_id: categoryId });
                      fetchDocumentFolders(categoryId);
                    }}
                  >
                    {documentCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      const categoryId = selectedFolder?.category_id || documentCategories[0]?.id;
                      setFolderForm(prev => ({ ...prev, category_id: categoryId }));
                      resetFolderForm();
                      setShowFolderModal(true);
                    }}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Neuer Ordner
                  </button>
                </div>
              </div>

              {/* Ordner-Hierarchie anzeigen */}
              <div className="row">
                {documentFolders.map(folder => (
                  <div key={folder.id} className="col-md-6 col-lg-4 mb-3">
                    <div className="card">
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-2">
                          <i 
                            className={`${folder.icon} me-2`} 
                            style={{ color: folder.color, fontSize: '1.2rem' }}
                          ></i>
                          <h6 className="card-title mb-0">{folder.name}</h6>
                        </div>
                        <p className="card-text small text-muted mb-2">
                          {folder.description || 'Keine Beschreibung'}
                        </p>
                        <p className="card-text small text-secondary mb-3">
                          <i className="bi bi-signpost me-1"></i>
                          {folder.path}
                          {folder.parent_name && (
                            <span className="ms-2">
                              <i className="bi bi-arrow-up-left me-1"></i>
                              {folder.parent_name}
                            </span>
                          )}
                        </p>
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            {internalDocuments.filter(doc => doc.folder_id === folder.id).length} Dokument(e)
                          </small>
                          <div>
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEditFolder(folder)}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-secondary me-2"
                              onClick={() => {
                                setFolderForm(prev => ({ 
                                  ...prev, 
                                  parent_id: folder.id,
                                  category_id: folder.category_id 
                                }));
                                setShowFolderModal(true);
                              }}
                            >
                              <i className="bi bi-folder-plus"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteFolder(folder.id)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                        
                        {/* Unterordner anzeigen */}
                        {folder.children && folder.children.length > 0 && (
                          <div className="mt-3 ps-3 border-start">
                            {folder.children.map(child => (
                              <div key={child.id} className="d-flex justify-content-between align-items-center py-1">
                                <span className="small">
                                  <i className={`${child.icon} me-1`} style={{ color: child.color }}></i>
                                  {child.name}
                                </span>
                                <div>
                                  <button
                                    className="btn btn-sm btn-outline-secondary me-1"
                                    onClick={() => handleEditFolder(child)}
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDeleteFolder(child.id)}
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Categories Management Tab */}
          {activeTab === 'categories' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">
                  <i className="bi bi-folder-fill me-2"></i>
                  Kategorien verwalten
                </h4>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    resetCategoryForm();
                    setShowCategoryModal(true);
                  }}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Neue Kategorie
                </button>
              </div>

              <div className="row">
                {documentCategories.map(category => (
                  <div key={category.id} className="col-md-6 col-lg-4 mb-3">
                    <div className="card">
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-2">
                          <i 
                            className={`${category.icon} me-2`} 
                            style={{ color: category.color, fontSize: '1.2rem' }}
                          ></i>
                          <h6 className="card-title mb-0">{category.name}</h6>
                        </div>
                        <p className="card-text small text-muted mb-3">
                          {category.description || 'Keine Beschreibung'}
                        </p>
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            {internalDocuments.filter(doc => doc.category === category.name).length} Dokument(e)
                          </small>
                          <div>
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEditCategory(category)}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteCategory(category.id)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Task Categories Management Tab */}
          {activeTab === 'task-categories' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">
                  <i className="bi bi-tags-fill me-2"></i>
                  Task-Kategorien verwalten
                </h4>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    resetTaskCategoryForm();
                    setShowTaskCategoryModal(true);
                  }}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Neue Task-Kategorie
                </button>
              </div>

              <div className="row">
                {taskCategories.map(category => (
                  <div key={category.id} className="col-md-6 col-lg-4 mb-3">
                    <div className="card">
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-2">
                          <i 
                            className={`${category.icon} me-2`} 
                            style={{ color: category.color, fontSize: '1.2rem' }}
                          ></i>
                          <h6 className="card-title mb-0">{category.name}</h6>
                          {category.is_system && (
                            <span className="badge bg-secondary ms-2">System</span>
                          )}
                        </div>
                        <p className="card-text small text-muted mb-3">
                          {category.description || 'Keine Beschreibung'}
                        </p>
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            {tasks.filter(task => task.category_id === category.id).length} Aufgabe(n)
                          </small>
                          <div>
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEditTaskCategory(category)}
                              disabled={category.is_system}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteTaskCategory(category.id)}
                              disabled={category.is_system}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {taskCategories.length === 0 && (
                <div className="text-center py-4">
                  <i className="bi bi-tags text-muted" style={{fontSize: '3rem'}}></i>
                  <p className="mt-2 text-muted">Noch keine Task-Kategorien vorhanden</p>
                  <p className="text-muted small">
                    Erstellen Sie Kategorien, um Ihre Aufgaben besser zu organisieren.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Task Reset Management Tab */}
          {activeTab === 'reset' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Jahres-Reset für Aufgaben
                </h4>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowResetModal(true)}
                >
                  <i className="bi bi-calendar-plus me-2"></i>
                  Reset konfigurieren
                </button>
              </div>

              <div className="row">
                <div className="col-md-8">
                  <div className="card">
                    <div className="card-header">
                      <h5 className="mb-0">
                        <i className="bi bi-info-circle me-2"></i>
                        Was bewirkt der Jahres-Reset?
                      </h5>
                    </div>
                    <div className="card-body">
                      <p>Der Jahres-Reset hilft Ihnen dabei, alle Aufgaben für das neue Jahr vorzubereiten:</p>
                      <ul className="list-unstyled">
                        <li><i className="bi bi-check-circle text-success me-2"></i><strong>Status zurücksetzen:</strong> Alle Aufgaben werden auf "Zu erledigen" gesetzt</li>
                        <li><i className="bi bi-check-circle text-success me-2"></i><strong>Fälligkeitsdaten anpassen:</strong> Automatische Anpassung der Termine auf das neue Jahr</li>
                        <li><i className="bi bi-check-circle text-success me-2"></i><strong>Zuweisungen behalten:</strong> Optional können Mitarbeiterzuweisungen beibehalten werden</li>
                        <li><i className="bi bi-check-circle text-success me-2"></i><strong>Kategorien filtern:</strong> Nur bestimmte Kategorien können zurückgesetzt werden</li>
                        <li><i className="bi bi-check-circle text-success me-2"></i><strong>Erledigte Aufgaben:</strong> Optional können bereits erledigte Aufgaben ausgeschlossen werden</li>
                      </ul>
                      
                      <div className="alert alert-warning">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        <strong>Hinweis:</strong> Der Reset kann nicht rückgängig gemacht werden. 
                        Verwenden Sie die Vorschau-Funktion, um die Änderungen vor der Ausführung zu überprüfen.
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-4">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">
                        <i className="bi bi-clipboard-data me-2"></i>
                        Aktuelle Statistiken
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <span>Gesamt Aufgaben:</span>
                          <strong>{tasks.length}</strong>
                        </div>
                      </div>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <span>Zu erledigen:</span>
                          <span className="badge bg-secondary">{tasks.filter(t => t.status === 'todo').length}</span>
                        </div>
                      </div>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <span>In Bearbeitung:</span>
                          <span className="badge bg-info">{tasks.filter(t => t.status === 'inprogress').length}</span>
                        </div>
                      </div>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <span>Erledigt:</span>
                          <span className="badge bg-success">{tasks.filter(t => t.status === 'completed').length}</span>
                        </div>
                      </div>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <span>Abgebrochen:</span>
                          <span className="badge bg-dark">{tasks.filter(t => t.status === 'cancelled').length}</span>
                        </div>
                      </div>
                      
                      <hr />
                      
                      <div className="mb-2">
                        <small className="text-muted">Kategorien: {taskCategories.length}</small>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted">
                          Überfällige Aufgaben: {tasks.filter(t => 
                            t.due_date && 
                            new Date(t.due_date) < new Date() && 
                            t.status !== 'completed'
                          ).length}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* Material Management Tab */}
          {activeTab === 'materials' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h4 className="mb-2">
                    <i className="bi bi-box-seam me-2"></i>
                    Material verwalten
                  </h4>
                  <div className="btn-group" role="group">
                    <input type="radio" className="btn-check" name="materialFilter" id="filterAll" 
                           checked={materialFilter === 'all'} onChange={() => setMaterialFilter('all')} />
                    <label className="btn btn-outline-primary" htmlFor="filterAll">
                      <i className="bi bi-list me-1"></i> Alle
                    </label>
                    
                    <input type="radio" className="btn-check" name="materialFilter" id="filterConsumable" 
                           checked={materialFilter === 'consumable'} onChange={() => setMaterialFilter('consumable')} />
                    <label className="btn btn-outline-success" htmlFor="filterConsumable">
                      <i className="bi bi-arrow-repeat me-1"></i> Verbrauchsmaterial
                    </label>
                    
                    <input type="radio" className="btn-check" name="materialFilter" id="filterInventory" 
                           checked={materialFilter === 'inventory'} onChange={() => setMaterialFilter('inventory')} />
                    <label className="btn btn-outline-warning" htmlFor="filterInventory">
                      <i className="bi bi-archive me-1"></i> Inventar
                    </label>
                  </div>
                </div>
                <div>
                  <button
                    className="btn btn-outline-secondary me-2"
                    onClick={() => {
                      resetMaterialCategoryForm();
                      setShowMaterialCategoryModal(true);
                    }}
                  >
                    <i className="bi bi-tags me-2"></i>
                    Kategorien verwalten
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      resetMaterialForm();
                      setShowMaterialModal(true);
                    }}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Neues Material
                  </button>
                </div>
              </div>

              {/* Material Cards */}
              <div className="row">
                {materials.length === 0 ? (
                  <div className="col-12">
                    <div className="text-center py-5">
                      <i className="bi bi-box-seam display-1 text-muted"></i>
                      <h4 className="text-muted mt-3">Keine Materialien gefunden</h4>
                      <p className="text-muted">
                        {materialFilter === 'all' 
                          ? 'Noch keine Materialien vorhanden. Erstellen Sie das erste Material.'
                          : `Keine ${materialFilter === 'consumable' ? 'Verbrauchsmaterialien' : 'Inventar-Gegenstände'} gefunden.`
                        }
                      </p>
                    </div>
                  </div>
                ) : (
                  materials.map(material => (
                    <div key={material.id} className="col-md-6 col-lg-4 mb-4">
                      <div className="card h-100">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <i className={`${material.category_color ? 'bi bi-circle-fill me-2' : 'bi bi-box me-2'}`} 
                               style={{ color: material.category_color || '#6c757d' }}></i>
                            <h6 className="card-title mb-0">{material.name}</h6>
                          </div>
                          <span className={`badge ${material.type === 'consumable' ? 'bg-success' : 'bg-warning'}`}>
                            {material.type === 'consumable' ? 'Verbrauch' : 'Inventar'}
                          </span>
                        </div>
                        <div className="card-body">
                          <p className="card-text small text-muted mb-2">
                            {material.description || 'Keine Beschreibung'}
                          </p>
                          
                          <div className="row g-2 mb-3">
                            <div className="col-6">
                              <strong className="small">Kategorie:</strong>
                              <br />
                              <span className="small text-secondary">{material.category_name}</span>
                            </div>
                            <div className="col-6">
                              <strong className="small">Aktueller Bestand:</strong>
                              <br />
                              <span className={`small ${material.min_stock && material.current_stock < material.min_stock ? 'text-danger fw-bold' : 'text-success'}`}>
                                {material.current_stock} {material.unit}
                                {material.min_stock && material.current_stock < material.min_stock && (
                                  <i className="bi bi-exclamation-triangle ms-1 text-danger"></i>
                                )}
                              </span>
                            </div>
                            {material.min_stock && (
                              <div className="col-6">
                                <strong className="small">Mindestbestand:</strong>
                                <br />
                                <span className="small text-secondary">{material.min_stock} {material.unit}</span>
                              </div>
                            )}
                            {material.max_stock && (
                              <div className="col-6">
                                <strong className="small">Maximalbestand:</strong>
                                <br />
                                <span className="small text-secondary">{material.max_stock} {material.unit}</span>
                              </div>
                            )}
                            {material.cost_per_unit && (
                              <div className="col-6">
                                <strong className="small">Kosten/Einheit:</strong>
                                <br />
                                <span className="small text-secondary">{material.cost_per_unit}€</span>
                              </div>
                            )}
                            {material.supplier && (
                              <div className="col-6">
                                <strong className="small">Lieferant:</strong>
                                <br />
                                <span className="small text-secondary">{material.supplier}</span>
                              </div>
                            )}
                            {material.location && (
                              <div className="col-12">
                                <strong className="small">Lagerort:</strong>
                                <br />
                                <span className="small text-secondary">{material.location}</span>
                              </div>
                            )}
                          </div>
                          
                          {material.last_counted && (
                            <div className="small text-muted mb-2">
                              <i className="bi bi-calendar-check me-1"></i>
                              Letzte Zählung: {formatDate(material.last_counted)}
                            </div>
                          )}
                          
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-outline-primary"
                                onClick={() => handleEditMaterial(material)}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => handleDeleteMaterial(material.id)}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                            
                            <div className="d-flex align-items-center">
                              <input
                                type="number"
                                className="form-control form-control-sm me-2"
                                style={{ width: '70px' }}
                                defaultValue={material.current_stock}
                                onBlur={(e) => {
                                  const newStock = parseFloat(e.target.value);
                                  if (!isNaN(newStock) && newStock !== material.current_stock) {
                                    handleUpdateStock(material.id, newStock);
                                  }
                                }}
                              />
                              <small className="text-muted">{material.unit}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Folder Modal */}
      {showFolderModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingFolder ? 'Ordner bearbeiten' : 'Neuen Ordner erstellen'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowFolderModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Ordnername *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={folderForm.name}
                    onChange={(e) => setFolderForm({ ...folderForm, name: e.target.value })}
                    placeholder="Name des Ordners"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Kategorie</label>
                  <select
                    className="form-select"
                    value={folderForm.category_id}
                    onChange={(e) => setFolderForm({ ...folderForm, category_id: e.target.value })}
                    disabled={editingFolder} // Kategorie kann bei existierenden Ordnern nicht geändert werden
                  >
                    <option value="">Kategorie wählen...</option>
                    {documentCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Übergeordneter Ordner */}
                <div className="mb-3">
                  <label className="form-label">Übergeordneter Ordner (optional)</label>
                  <select
                    className="form-select"
                    value={folderForm.parent_id}
                    onChange={(e) => setFolderForm({ ...folderForm, parent_id: e.target.value })}
                  >
                    <option value="">Hauptverzeichnis</option>
                    {documentFolders
                      .filter(folder => !editingFolder || folder.id !== editingFolder.id) // Sich selbst ausschließen
                      .map(folder => (
                        <option key={folder.id} value={folder.id}>
                          {folder.path}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Beschreibung</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={folderForm.description}
                    onChange={(e) => setFolderForm({ ...folderForm, description: e.target.value })}
                    placeholder="Beschreibung des Ordners..."
                  ></textarea>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Farbe</label>
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={folderForm.color}
                        onChange={(e) => setFolderForm({ ...folderForm, color: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Icon</label>
                      <select
                        className="form-select"
                        value={folderForm.icon}
                        onChange={(e) => setFolderForm({ ...folderForm, icon: e.target.value })}
                      >
                        {availableIcons.map(icon => (
                          <option key={icon} value={icon}>
                            {icon.replace('bi-', '')}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Vorschau */}
                <div className="p-3 bg-light rounded">
                  <h6>Vorschau:</h6>
                  <div className="d-flex align-items-center">
                    <i 
                      className={`${folderForm.icon} me-2`} 
                      style={{ color: folderForm.color, fontSize: '1.5rem' }}
                    ></i>
                    <div>
                      <strong>{folderForm.name || 'Ordnername'}</strong>
                      <br />
                      <small className="text-muted">
                        {folderForm.description || 'Keine Beschreibung'}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowFolderModal(false)}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveFolder}
                  disabled={!folderForm.name || !folderForm.category_id}
                >
                  {editingFolder ? 'Aktualisieren' : 'Erstellen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingCategory ? 'Kategorie bearbeiten' : 'Neue Kategorie'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCategoryModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    placeholder="Kategorie-Name"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Beschreibung</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                    placeholder="Beschreibung der Kategorie..."
                  />
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label">Farbe</label>
                    <input
                      type="color"
                      className="form-control form-control-color"
                      value={categoryForm.color}
                      onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Icon</label>
                    <select
                      className="form-select"
                      value={categoryForm.icon}
                      onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                    >
                      {availableIcons.map(icon => (
                        <option key={icon} value={icon}>
                          {icon.replace('bi-', '')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-3 p-3 bg-light rounded">
                  <strong>Vorschau:</strong>
                  <div className="d-flex align-items-center mt-2">
                    <i 
                      className={`${categoryForm.icon} me-2`} 
                      style={{ color: categoryForm.color, fontSize: '1.2rem' }}
                    ></i>
                    <span>{categoryForm.name || 'Kategorie-Name'}</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCategoryModal(false)}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveCategory}
                >
                  {editingCategory ? 'Aktualisieren' : 'Erstellen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Category Modal */}
      {showTaskCategoryModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingTaskCategory ? 'Task-Kategorie bearbeiten' : 'Neue Task-Kategorie'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowTaskCategoryModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={taskCategoryForm.name}
                    onChange={(e) => setTaskCategoryForm({ ...taskCategoryForm, name: e.target.value })}
                    placeholder="Kategorie-Name"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Beschreibung</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={taskCategoryForm.description}
                    onChange={(e) => setTaskCategoryForm({ ...taskCategoryForm, description: e.target.value })}
                    placeholder="Beschreibung der Kategorie..."
                  />
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label">Farbe</label>
                    <input
                      type="color"
                      className="form-control form-control-color"
                      value={taskCategoryForm.color}
                      onChange={(e) => setTaskCategoryForm({ ...taskCategoryForm, color: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Icon</label>
                    <select
                      className="form-select"
                      value={taskCategoryForm.icon}
                      onChange={(e) => setTaskCategoryForm({ ...taskCategoryForm, icon: e.target.value })}
                    >
                      {availableIcons.map(icon => (
                        <option key={icon} value={icon}>
                          {icon.replace('bi-', '')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-3 p-3 bg-light rounded">
                  <strong>Vorschau:</strong>
                  <div className="d-flex align-items-center mt-2">
                    <i 
                      className={`${taskCategoryForm.icon} me-2`} 
                      style={{ color: taskCategoryForm.color, fontSize: '1.2rem' }}
                    ></i>
                    <span>{taskCategoryForm.name || 'Kategorie-Name'}</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowTaskCategoryModal(false)}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveTaskCategory}
                  disabled={!taskCategoryForm.name}
                >
                  {editingTaskCategory ? 'Aktualisieren' : 'Erstellen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Modal */}
      {showResetModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Jahres-Reset konfigurieren
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowResetModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Zieljahr *</label>
                      <input
                        type="number"
                        className="form-control"
                        value={resetForm.year}
                        onChange={(e) => setResetForm({ ...resetForm, year: parseInt(e.target.value) })}
                        min={new Date().getFullYear()}
                        max={new Date().getFullYear() + 5}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Kategorien</label>
                      <select
                        className="form-select"
                        value={resetForm.categories}
                        onChange={(e) => setResetForm({ ...resetForm, categories: e.target.value })}
                      >
                        <option value="all">Alle Kategorien</option>
                        {taskCategories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <h6>Reset-Optionen:</h6>
                      
                      <div className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="resetStatus"
                          checked={resetForm.resetStatus}
                          onChange={(e) => setResetForm({ ...resetForm, resetStatus: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="resetStatus">
                          Status auf "Zu erledigen" zurücksetzen
                        </label>
                      </div>

                      <div className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="adjustDueDates"
                          checked={resetForm.adjustDueDates}
                          onChange={(e) => setResetForm({ ...resetForm, adjustDueDates: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="adjustDueDates">
                          Fälligkeitsdaten automatisch anpassen
                        </label>
                      </div>

                      <div className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="resetAssignments"
                          checked={resetForm.resetAssignments}
                          onChange={(e) => setResetForm({ ...resetForm, resetAssignments: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="resetAssignments">
                          Mitarbeiterzuweisungen entfernen
                        </label>
                      </div>

                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="preserveCompleted"
                          checked={resetForm.preserveCompleted}
                          onChange={(e) => setResetForm({ ...resetForm, preserveCompleted: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="preserveCompleted">
                          Bereits erledigte Aufgaben auslassen
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {resetPreview ? (
                  <div className="alert alert-info">
                    <i className="bi bi-eye me-2"></i>
                    <strong>Vorschau:</strong> {resetPreview.affectedTasks} Aufgabe{resetPreview.affectedTasks === 1 ? '' : 'n'} betroffen
                    {resetForm.resetStatus && ' · Status wird auf "Zu erledigen" gesetzt'}
                    {resetForm.adjustDueDates && ' · Fälligkeitsdaten werden um 1 Jahr verschoben'}
                    {resetForm.resetAssignments && ' · Zuweisungen werden entfernt'}
                  </div>
                ) : (
                  <div className="alert alert-secondary">
                    <i className="bi bi-hourglass-split me-2"></i>
                    Vorschau wird geladen…
                  </div>
                )}

                <div className="alert alert-warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <strong>Wichtiger Hinweis:</strong> Diese Aktion kann nicht rückgängig gemacht werden.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowResetModal(false)}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleExecuteReset}
                  disabled={!resetPreview}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Reset ausführen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Modal */}
      {showTaskModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingTask ? `Aufgabe #${editingTask.id} bearbeiten` : 'Neue Aufgabe'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowTaskModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Titel *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={taskForm.title}
                      onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Kategorie</label>
                    <select
                      className="form-select"
                      value={taskForm.category_id}
                      onChange={(e) => setTaskForm({ ...taskForm, category_id: e.target.value })}
                    >
                      <option value="">Kategorie wählen...</option>
                      {taskCategories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Beschreibung</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={taskForm.description}
                    onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                    placeholder="Detaillierte Beschreibung der Aufgabe oder des Ablaufs..."
                  ></textarea>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Priorität</label>
                    <select
                      className="form-select"
                      value={taskForm.priority}
                      onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                    >
                      {Object.entries(priorities).map(([key, info]) => (
                        <option key={key} value={key}>
                          {info.icon} {info.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={taskForm.status}
                      onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}
                    >
                      {Object.entries(statuses).map(([key, info]) => (
                        <option key={key} value={key}>
                          {info.icon} {info.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Fälligkeitsdatum</label>
                    <input
                      type="date"
                      className="form-control"
                      value={taskForm.due_date}
                      onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Zuständig</label>
                    <select
                      className="form-select"
                      value={taskForm.assigned_to}
                      onChange={(e) => setTaskForm({ ...taskForm, assigned_to: e.target.value })}
                    >
                      <option value="">– niemand –</option>
                      {assignableUsers.some((u) => u.role_id === 1) && (
                        <optgroup label="Leitung">
                          {assignableUsers.filter((u) => u.role_id === 1).map((u) => (
                            <option key={u.id} value={u.id}>{u.firstname} {u.lastname}</option>
                          ))}
                        </optgroup>
                      )}
                      <optgroup label="Mitarbeiter">
                        {assignableUsers.filter((u) => u.role_id !== 1).map((u) => (
                          <option key={u.id} value={u.id}>{u.firstname} {u.lastname}</option>
                        ))}
                      </optgroup>
                    </select>
                    <div className="form-text">Bei neuer Zuweisung geht automatisch eine WhatsApp an die Person.</div>
                  </div>
                </div>

                {/* Notizen-Verlauf (nur bei bestehenden Aufgaben) */}
                {editingTask && (
                  <div className="border-top pt-3">
                    <label className="form-label mb-2">
                      📝 Notizen {taskNotes.length > 0 && <span className="text-muted">({taskNotes.length})</span>}
                    </label>
                    {taskNotes.length > 0 && (
                      <div className="mb-2" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                        {taskNotes.map((n) => (
                          <div key={n.id} className="d-flex align-items-start gap-2 border rounded p-2 mb-1 bg-light">
                            <div className="flex-grow-1">
                              <div style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{n.note}</div>
                              <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                {n.author || 'Unbekannt'} · {n.created_at ? new Date(String(n.created_at).replace(' ', 'T') + 'Z').toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                              </div>
                            </div>
                            <button
                              className="btn btn-sm btn-light border py-0 px-1 text-danger"
                              title="Notiz löschen"
                              onClick={() => handleDeleteNote(n.id)}
                            >
                              <i className="bi bi-trash" style={{ fontSize: '0.7rem' }}></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="input-group input-group-sm">
                      <input
                        className="form-control"
                        placeholder="Neue Notiz… (z.B. Problem: schwer zu transportieren)"
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddNote(); }}
                      />
                      <button className="btn btn-outline-primary" onClick={handleAddNote} disabled={!newNote.trim()}>
                        Hinzufügen
                      </button>
                    </div>
                    <div className="form-text">
                      Geht auch per WhatsApp: <code>Notiz #{editingTask.id}: &lt;Text&gt;</code>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowTaskModal(false)}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveTask}
                >
                  {editingTask ? 'Aktualisieren' : 'Erstellen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showDocModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Dokument hochladen
                  {draggedFiles.length > 0 && (
                    <span className="badge bg-info ms-2">{draggedFiles.length} Datei(en)</span>
                  )}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDocModal(false);
                    setDraggedFiles([]);
                    setUploadFile(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {/* Drag & Drop Dateien anzeigen */}
                {draggedFiles.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label">
                      <i className="bi bi-files me-2"></i>
                      Ausgewählte Dateien via Drag & Drop ({draggedFiles.length})
                    </label>
                    <div className="list-group mb-3" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                      {draggedFiles.map((file, index) => (
                        <div key={index} className="list-group-item d-flex justify-content-between align-items-center py-2">
                          <div className="d-flex align-items-center">
                            <i className="bi bi-file-earmark me-2"></i>
                            <span className="small">{file.name}</span>
                          </div>
                          <span className="badge bg-secondary small">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Standard-Dateiauswahl (wenn keine Drag & Drop Dateien) */}
                {draggedFiles.length === 0 && (
                  <div className="mb-3">
                    <label className="form-label">Datei auswählen *</label>
                    <input
                      type="file"
                      className="form-control"
                      multiple
                      onChange={(e) => {
                        if (e.target.files.length > 1) {
                          setDraggedFiles(Array.from(e.target.files));
                          setUploadFile(e.target.files[0]);
                        } else {
                          setUploadFile(e.target.files[0]);
                        }
                      }}
                    />
                    <small className="form-text text-muted">
                      Sie können mehrere Dateien gleichzeitig auswählen
                    </small>
                  </div>
                )}
                
                <div className="mb-3">
                  <label className="form-label">Kategorie</label>
                  <select
                    className="form-select"
                    value={docForm.category}
                    onChange={(e) => setDocForm({ ...docForm, category: e.target.value })}
                  >
                    <option value="">Kategorie wählen...</option>
                    {documentCategories.map(category => (
                      <option key={category.id} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Ordner-Auswahl (nur wenn Kategorie ausgewählt) */}
                {docForm.category && documentFolders.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label">Ordner (optional)</label>
                    <select
                      className="form-select"
                      value={docForm.folder_id}
                      onChange={(e) => setDocForm({ ...docForm, folder_id: e.target.value })}
                    >
                      <option value="">Kein Ordner (in Hauptverzeichnis ablegen)</option>
                      {documentFolders.map(folder => (
                        <option key={folder.id} value={folder.id}>
                          {folder.path}
                          {folder.children && folder.children.map(child => (
                            <option key={child.id} value={child.id}>
                              {child.path}
                            </option>
                          ))}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Beschreibung</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={docForm.description}
                    onChange={(e) => setDocForm({ ...docForm, description: e.target.value })}
                    placeholder="Beschreibung des Dokuments..."
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDocModal(false);
                    setDraggedFiles([]);
                    setUploadFile(null);
                  }}
                >
                  Abbrechen
                </button>
                {draggedFiles.length > 0 ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleBulkUpload}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Lade hoch...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-cloud-upload me-2"></i>
                        {draggedFiles.length} Datei(en) hochladen
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleUploadDocument}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Lade hoch...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-upload me-2"></i>
                        Hochladen
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Material Modal */}
      {showMaterialModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingMaterial ? 'Material bearbeiten' : 'Neues Material erstellen'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowMaterialModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={materialForm.name}
                        onChange={(e) => setMaterialForm({ ...materialForm, name: e.target.value })}
                        placeholder="Name des Materials"
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Kategorie *</label>
                      <select
                        className="form-select"
                        value={materialForm.category_id}
                        onChange={(e) => setMaterialForm({ ...materialForm, category_id: e.target.value })}
                      >
                        <option value="">Kategorie wählen</option>
                        {materialCategories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name} ({category.type === 'consumable' ? 'Verbrauch' : 'Inventar'})
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Typ *</label>
                      <select
                        className="form-select"
                        value={materialForm.type}
                        onChange={(e) => setMaterialForm({ ...materialForm, type: e.target.value })}
                      >
                        <option value="consumable">Verbrauchsmaterial</option>
                        <option value="inventory">Inventar</option>
                      </select>
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Einheit</label>
                      <input
                        type="text"
                        className="form-control"
                        value={materialForm.unit}
                        onChange={(e) => setMaterialForm({ ...materialForm, unit: e.target.value })}
                        placeholder="z.B. Stück, kg, Liter"
                      />
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Aktueller Bestand</label>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        value={materialForm.current_stock}
                        onChange={(e) => setMaterialForm({ ...materialForm, current_stock: e.target.value })}
                        placeholder="0"
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Mindestbestand</label>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        value={materialForm.min_stock}
                        onChange={(e) => setMaterialForm({ ...materialForm, min_stock: e.target.value })}
                        placeholder="Warnschwelle"
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Maximalbestand</label>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        value={materialForm.max_stock}
                        onChange={(e) => setMaterialForm({ ...materialForm, max_stock: e.target.value })}
                        placeholder="Maximaler Lagerbestand"
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Kosten pro Einheit (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={materialForm.cost_per_unit}
                        onChange={(e) => setMaterialForm({ ...materialForm, cost_per_unit: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Lieferant</label>
                      <input
                        type="text"
                        className="form-control"
                        value={materialForm.supplier}
                        onChange={(e) => setMaterialForm({ ...materialForm, supplier: e.target.value })}
                        placeholder="Name des Lieferanten"
                      />
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Lagerort</label>
                      <input
                        type="text"
                        className="form-control"
                        value={materialForm.location}
                        onChange={(e) => setMaterialForm({ ...materialForm, location: e.target.value })}
                        placeholder="Wo wird es gelagert?"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Beschreibung</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={materialForm.description}
                    onChange={(e) => setMaterialForm({ ...materialForm, description: e.target.value })}
                    placeholder="Detaillierte Beschreibung des Materials..."
                  ></textarea>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Notizen</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={materialForm.notes}
                    onChange={(e) => setMaterialForm({ ...materialForm, notes: e.target.value })}
                    placeholder="Zusätzliche Notizen..."
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowMaterialModal(false)}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveMaterial}
                >
                  {editingMaterial ? 'Aktualisieren' : 'Erstellen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Material Category Modal */}
      {showMaterialCategoryModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-tags me-2"></i>
                  Material-Kategorien verwalten
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowMaterialCategoryModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                
                {/* Liste der bestehenden Kategorien */}
                {!editingMaterialCategory && (
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Bestehende Kategorien</h6>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          resetMaterialCategoryForm();
                          setEditingMaterialCategory({ id: 'new' });
                        }}
                      >
                        <i className="bi bi-plus me-1"></i>
                        Neue Kategorie
                      </button>
                    </div>
                    
                    <div className="list-group">
                      {materialCategories.map(category => (
                        <div key={category.id} className="list-group-item d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <i className={category.icon} style={{ color: category.color }} ></i>
                            <div className="ms-3">
                              <div className="fw-bold">{category.name}</div>
                              <small className="text-muted">
                                {category.description} 
                                <span className={`badge ms-2 ${category.type === 'consumable' ? 'bg-success' : 'bg-warning'}`}>
                                  {category.type === 'consumable' ? 'Verbrauch' : 'Inventar'}
                                </span>
                              </small>
                            </div>
                          </div>
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => handleEditMaterialCategory(category)}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => handleDeleteMaterialCategory(category.id)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Formular für neue/bearbeitete Kategorie */}
                {editingMaterialCategory && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        {editingMaterialCategory.id === 'new' ? 'Neue Material-Kategorie erstellen' : 'Material-Kategorie bearbeiten'}
                      </h6>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setEditingMaterialCategory(null)}
                      >
                        <i className="bi bi-arrow-left me-1"></i>
                        Zurück zur Liste
                      </button>
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={materialCategoryForm.name}
                        onChange={(e) => setMaterialCategoryForm({ ...materialCategoryForm, name: e.target.value })}
                        placeholder="Name der Kategorie"
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Typ *</label>
                      <select
                        className="form-select"
                        value={materialCategoryForm.type}
                        onChange={(e) => setMaterialCategoryForm({ ...materialCategoryForm, type: e.target.value })}
                      >
                        <option value="consumable">Verbrauchsmaterial</option>
                        <option value="inventory">Inventar</option>
                      </select>
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Beschreibung</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={materialCategoryForm.description}
                        onChange={(e) => setMaterialCategoryForm({ ...materialCategoryForm, description: e.target.value })}
                        placeholder="Beschreibung der Kategorie..."
                      ></textarea>
                    </div>
                    
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Farbe</label>
                          <input
                            type="color"
                            className="form-control form-control-color"
                            value={materialCategoryForm.color}
                            onChange={(e) => setMaterialCategoryForm({ ...materialCategoryForm, color: e.target.value })}
                          />
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Icon</label>
                          <select
                            className="form-select"
                            value={materialCategoryForm.icon}
                            onChange={(e) => setMaterialCategoryForm({ ...materialCategoryForm, icon: e.target.value })}
                          >
                            {availableIcons.map(icon => (
                              <option key={icon} value={icon}>
                                {icon.replace('bi-', '')}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowMaterialCategoryModal(false)}
                >
                  {editingMaterialCategory ? 'Abbrechen' : 'Schließen'}
                </button>
                {editingMaterialCategory && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveMaterialCategory}
                  >
                    {editingMaterialCategory.id === 'new' ? 'Erstellen' : 'Aktualisieren'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expense Management Tab */}
      {activeTab === 'expenses' && (
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Auslagen verwalten</h5>
              <button
                className="btn btn-primary"
                onClick={() => {
                  resetExpenseForm();
                  setShowExpenseModal(true);
                }}
              >
                <i className="bi bi-plus me-2"></i>
                Neue Auslage hinzufügen
              </button>
            </div>

            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Lade...</span>
                </div>
              </div>
            ) : (
              <div>
                {/* Auslagen-Übersicht */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header bg-primary text-white">
                        <h6 className="mb-0">
                          <i className="bi bi-bar-chart me-2"></i>
                          Auslagen-Übersicht
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          {['Philipp', 'Daniel', 'Peter'].map(person => {
                            const personExpenses = expenses.filter(expense => 
                              expense.from_person === person && expense.amount
                            );
                            const totalAmount = personExpenses.reduce((sum, expense) => 
                              sum + parseFloat(expense.amount || 0), 0
                            );
                            const completedAmount = personExpenses
                              .filter(expense => expense.completed)
                              .reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
                            const openAmount = totalAmount - completedAmount;
                            
                            return (
                              <div key={person} className="col-md-4 mb-3">
                                <div className="card h-100 border-info">
                                  <div className="card-body text-center">
                                    <h5 className="card-title text-info">
                                      <i className="bi bi-person-circle me-2"></i>
                                      {person}
                                    </h5>
                                    <div className="mt-3">
                                      <div className="mb-2">
                                        <span className="badge bg-success me-2">Gesamt:</span>
                                        <strong>{totalAmount.toFixed(2)} €</strong>
                                      </div>
                                      <div className="mb-2">
                                        <span className="badge bg-primary me-2">Erledigt:</span>
                                        <span>{completedAmount.toFixed(2)} €</span>
                                      </div>
                                      <div className="mb-2">
                                        <span className="badge bg-warning me-2">Offen:</span>
                                        <span>{openAmount.toFixed(2)} €</span>
                                      </div>
                                      <div className="small text-muted">
                                        {personExpenses.length} Auslage(n)
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Gesamtübersicht */}
                        <div className="row mt-3">
                          <div className="col-12">
                            <div className="card bg-light">
                              <div className="card-body">
                                <div className="row text-center">
                                  <div className="col-md-4">
                                    <h5 className="text-success">
                                      {expenses
                                        .filter(expense => expense.amount)
                                        .reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0)
                                        .toFixed(2)} €
                                    </h5>
                                    <small className="text-muted">Gesamt alle Auslagen</small>
                                  </div>
                                  <div className="col-md-4">
                                    <h5 className="text-primary">
                                      {expenses
                                        .filter(expense => expense.completed && expense.amount)
                                        .reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0)
                                        .toFixed(2)} €
                                    </h5>
                                    <small className="text-muted">Erledigte Auslagen</small>
                                  </div>
                                  <div className="col-md-4">
                                    <h5 className="text-warning">
                                      {expenses
                                        .filter(expense => !expense.completed && expense.amount)
                                        .reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0)
                                        .toFixed(2)} €
                                    </h5>
                                    <small className="text-muted">Offene Auslagen</small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Einzelne Auslagen-Karten */}
                <div className="row mb-3">
                  <div className="col-12">
                    <h6 className="mb-0">
                      <i className="bi bi-list me-2"></i>
                      Alle Auslagen
                    </h6>
                  </div>
                </div>
              
                <div className="row">
                {expenses.length === 0 ? (
                  <div className="col-12">
                    <div className="alert alert-info text-center">
                      <i className="bi bi-info-circle me-2"></i>
                      Noch keine Auslagen vorhanden. Klicken Sie auf "Neue Auslage hinzufügen", um zu beginnen.
                    </div>
                  </div>
                ) : (
                  expenses.map(expense => (
                    <div key={expense.id} className="col-md-4 mb-4">
                      <div className={`card h-100 ${expense.completed ? 'border-success' : 'border-warning'}`}>
                        <div className={`card-header ${expense.completed ? 'bg-success text-white' : 'bg-warning text-dark'}`}>
                          <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">
                              <i className="bi bi-person me-2"></i>
                              {expense.from_person}
                            </h6>
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={expense.completed}
                                onChange={() => handleToggleExpenseStatus(expense.id, expense.completed)}
                                style={{ cursor: 'pointer' }}
                              />
                              <label className="form-check-label small">
                                {expense.completed ? 'Erledigt' : 'Offen'}
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="card-body">
                          <p className="card-text">
                            <strong>Grund:</strong><br />
                            {expense.reason}
                          </p>
                          <p className="card-text">
                            <small className="text-muted">
                              <i className="bi bi-calendar me-1"></i>
                              {new Date(expense.date).toLocaleDateString('de-DE')}
                            </small>
                          </p>
                          {expense.amount && (
                            <p className="card-text">
                              <strong>Betrag:</strong> {expense.amount} €
                            </p>
                          )}
                        </div>
                        <div className="card-footer bg-transparent">
                          <div className="d-flex justify-content-between">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEditExpense(expense)}
                              title="Bearbeiten"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteExpense(expense.id)}
                              title="Löschen"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Beschilderung Tab */}
      {activeTab === 'signage' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3>🏷️ Beschilderung</h3>
            <div className="text-muted">
              <small>Erstellen Sie DIN A4 Schilder und generieren Sie PDFs zum Ausdrucken</small>
            </div>
          </div>

          <SignageManager />
        </div>
      )}

      {/* Station Planner Tab */}
      {activeTab === 'station-planner' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3>🏗️ Station Layout Planer</h3>
            <div className="btn-group">
              <select 
                className="form-select me-2" 
                value={selectedStation}
                onChange={(e) => setSelectedStation(e.target.value)}
                style={{ width: 'auto' }}
              >
                <option value="G1">Station G1</option>
                <option value="G5">Station G5</option>
                <option value="G8">Station G8</option>
                <option value="G10">Station G10</option>
              </select>
              <button className="btn btn-outline-info btn-sm">
                <i className="bi bi-question-circle"></i>
                Hilfe
              </button>
            </div>
          </div>

          <StationPlanner2D station={selectedStation} user={user} />
        </div>
      )}

      {/* Fence Modal */}
      {showFenceModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingFence ? 'Bauzaun bearbeiten' : 'Neuen Bauzaun hinzufügen'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowFenceModal(false);
                    setEditingFence(null);
                    setFenceForm({
                      station: selectedStation,
                      number_from: '',
                      number_to: '',
                      x: 0,
                      y: 0,
                      width: 100,
                      height: 30,
                      is_short: false,
                      color: '#ffc107'
                    });
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Station</label>
                      <select
                        className="form-select"
                        value={fenceForm.station}
                        onChange={(e) => setFenceForm({ ...fenceForm, station: e.target.value })}
                      >
                        <option value="G1">G1</option>
                        <option value="G5">G5</option>
                        <option value="G8">G8</option>
                        <option value="G10">G10</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="fenceIsShort"
                          checked={fenceForm.is_short}
                          onChange={(e) => setFenceForm({ ...fenceForm, is_short: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="fenceIsShort">
                          Kurzer Bauzaun
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Nummer von *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={fenceForm.number_from}
                        onChange={(e) => setFenceForm({ ...fenceForm, number_from: e.target.value })}
                        placeholder="z.B. 001"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Nummer bis (optional)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={fenceForm.number_to}
                        onChange={(e) => setFenceForm({ ...fenceForm, number_to: e.target.value })}
                        placeholder="z.B. 005 (für kombinierte Zäune)"
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">X-Position</label>
                      <input
                        type="number"
                        className="form-control"
                        value={fenceForm.x}
                        onChange={(e) => setFenceForm({ ...fenceForm, x: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Y-Position</label>
                      <input
                        type="number"
                        className="form-control"
                        value={fenceForm.y}
                        onChange={(e) => setFenceForm({ ...fenceForm, y: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Breite</label>
                      <input
                        type="number"
                        className="form-control"
                        value={fenceForm.width}
                        onChange={(e) => setFenceForm({ ...fenceForm, width: parseInt(e.target.value) || 100 })}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Höhe</label>
                      <input
                        type="number"
                        className="form-control"
                        value={fenceForm.height}
                        onChange={(e) => setFenceForm({ ...fenceForm, height: parseInt(e.target.value) || 30 })}
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Farbe</label>
                  <input
                    type="color"
                    className="form-control form-control-color"
                    value={fenceForm.color}
                    onChange={(e) => setFenceForm({ ...fenceForm, color: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowFenceModal(false);
                    setEditingFence(null);
                  }}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveFence}
                >
                  {editingFence ? 'Aktualisieren' : 'Hinzufügen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expense Modal */}
      {showExpenseModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingExpense ? 'Auslage bearbeiten' : 'Neue Auslage hinzufügen'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowExpenseModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Von *</label>
                      <select
                        className="form-select"
                        value={expenseForm.from_person}
                        onChange={(e) => setExpenseForm({ ...expenseForm, from_person: e.target.value })}
                        required
                      >
                        <option value="">Bitte wählen...</option>
                        <option value="Philipp">Philipp</option>
                        <option value="Daniel">Daniel</option>
                        <option value="Peter">Peter</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Datum *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={expenseForm.date}
                        onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Grund *</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={expenseForm.reason}
                    onChange={(e) => setExpenseForm({ ...expenseForm, reason: e.target.value })}
                    placeholder="Beschreibung der Auslage..."
                    required
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label className="form-label">Betrag (optional)</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      value={expenseForm.amount}
                      onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                    <span className="input-group-text">€</span>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="expenseCompleted"
                      checked={expenseForm.completed}
                      onChange={(e) => setExpenseForm({ ...expenseForm, completed: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="expenseCompleted">
                      Als erledigt markieren
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowExpenseModal(false)}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveExpense}
                >
                  {editingExpense ? 'Aktualisieren' : 'Hinzufügen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TasksAndPlanning;
